import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  Package, Database, RefreshCw, Layers, ShieldCheck, FileText, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const WismoPage = () => {
  const benefits = [
    {
      icon: Layers,
      title: 'Omnichannel automation',
      description:
        'Deliver consistent answers across voice, chat, and email so customers get the same accurate status and options regardless of which channel they use.',
    },
    {
      icon: Database,
      title: 'Real-time order tracking retrieval',
      description:
        'Pull live order status, ETA, and carrier tracking data directly from OMS, ERP, and carrier APIs at the moment of the inquiry - no batch delays.',
    },
    {
      icon: RefreshCw,
      title: 'Automated returns + human handoff',
      description:
        'Initiate return, exchange, and refund workflows end-to-end based on policy rules, and escalate exceptions to agents with a full conversation summary.',
    },
  ];

  const capabilities = [
    'Connect CRM + OMS/ERP + Carrier tracking APIs + returns/refund tools',
    'Define return windows, refund rules, address changes, and exceptions',
    'Configure WISMO, delivery change, return initiation, and refund status intents',
    'Knowledge grounding: shipping FAQs, return policy, escalation paths',
    'Security: verification rules, PII controls, audit logs',
  ];

  const features = [
    {
      icon: Database,
      title: 'Real-Time Order Lookup',
      desc: 'Query OMS, ERP, and carrier APIs in real time to retrieve live order status, ETA, and shipment location at the moment of the customer inquiry.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: RefreshCw,
      title: 'Returns & Refund Automation',
      desc: 'Evaluate return eligibility against policy rules and initiate return labels, exchanges, or refund requests without agent involvement.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: Layers,
      title: 'Omnichannel Consistency',
      desc: 'Serve identical answers and actions across voice IVR, web chat, SMS, and email - a unified resolution experience regardless of channel.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: ShieldCheck,
      title: 'Identity Verification',
      desc: 'Verify customers via OTP, email, SMS, or login token before exposing order data or taking account actions, with configurable risk thresholds.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: FileText,
      title: 'ITSM Case Creation',
      desc: 'Automatically create or update CRM cases for every interaction, capturing intent, resolution action, and outcome for full audit readiness.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: ArrowRight,
      title: 'Smart Escalation',
      desc: 'Route edge cases and policy exceptions to a human agent with a pre-populated summary - context, verified identity, and attempted resolution steps included.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Customer contacts via voice, chat, or email',
      desc: 'The AI agent receives the inbound inquiry across any configured channel and identifies the interaction as an order support request.',
    },
    {
      step: '02',
      title: 'AI verifies identity',
      desc: 'The agent validates the customer using OTP, email, SMS, or login token based on configured verification policy before accessing order data.',
    },
    {
      step: '03',
      title: 'AI pulls order and tracking status in real time',
      desc: 'Live data is retrieved from OMS, ERP, and carrier APIs to provide the most current status, ETA, and shipment location.',
    },
    {
      step: '04',
      title: 'AI answers ETA and status, offers next actions',
      desc: 'The agent presents the status clearly and proactively offers options: delivery change, return initiation, or refund status check.',
    },
    {
      step: '05',
      title: 'AI initiates return, exchange, or refund workflows',
      desc: 'If the customer opts in, the agent executes the selected action against configured policy rules - no agent involvement required for standard cases.',
    },
    {
      step: '06',
      title: 'Escalates exceptions to a human with full summary',
      desc: 'When a case falls outside policy or verification fails, the agent transfers to a live agent with complete context, verified identity, and actions taken.',
    },
  ];

  const stats = [
    { value: '3×', label: 'Faster response time' },
    { value: '+22%', label: 'Higher CSAT' },
    { value: '+20pts', label: 'FCR improvement (65%→85%)' },
    { value: '−60%', label: 'AHT reduction (8min→3.2min)' },
  ];

  const faqs = [
    {
      q: 'Can it handle complex return policies?',
      a: 'Yes. Return window rules, exclusions, restocking fees, region-specific policies, and exception overrides are all configurable in the policy layer. The AI evaluates each case against the active ruleset before taking action.',
    },
    {
      q: 'What if tracking data is missing or the carrier API is unavailable?',
      a: 'The agent detects data gaps and applies fallback responses - informing the customer of the delay and offering alternative resolution paths (e.g., opening a case, escalating to an agent) rather than providing incorrect information.',
    },
    {
      q: 'Does it work with any CRM or OMS?',
      a: 'Digian.ai integrates with major CRMs (Salesforce, ServiceNow, Zendesk) and OMS/ERP platforms (SAP, Oracle, NetSuite, Microsoft Dynamics). Custom connectors are available for proprietary systems.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseOrderStatus" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Order Status","item":"https://digian.ai/use-cases/order-status"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">Customer Care / Order Status &amp; Returns</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              AI Order Support: Status, Delivery Updates &amp; Returns - 24/7 Omnichannel
            </h1>
            <p className="text-lg text-slate-600">
              High-volume customer inquiries like "Where is my order?", delivery changes, return eligibility, and refund status are automated across voice, chat, and email, with real-time actions in CRM, OMS/ERP, and shipping systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['Omnichannel voice + chat + email', 'Real-time carrier data', 'Policy-based returns'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Package className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">WISMO Automation</p>
                <p className="text-sm text-slate-600">End-to-end order support across voice, chat &amp; email</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <Package className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why teams choose AI order support</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate order support</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How AI order support works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">Automate your highest-volume support channel</h2>
            <p className="text-xl text-indigo-100">
              ~70% lower cost per interaction. ~$2.8M annual savings per 100 agents. Payback in 9–14 months.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">Omnichannel · Real-time data · Policy-based automation</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WismoPage;
